<template>
  <v-text-field
    ref="input"
    :value="texto"
    :label="translatedLabel"
    :name="name"
    :placeholder="placeholderText"
    :hint="hintText"
    persistent-hint
    :rules="validationRules"
    :error-messages="errorMessages"
    :inputmode="modo === MODO_DECIMAL ? 'decimal' : 'text'"
    :maxlength="modo === MODO_DECIMAL ? 12 : 8"
    autocomplete="off"
    autocorrect="off"
    autocapitalize="off"
    spellcheck="false"
    :aria-invalid="errorMessages.length ? 'true' : 'false'"
    :aria-required="obrigatorio ? 'true' : 'false'"
    :disabled="disabled"
    hide-details="auto"
    class="
      campo-quantidade-horas
      input-style
      font-size-input
      text-light-input
      placeholder-light
      input-icon
    "
    dense
    flat
    filled
    solo
    @input="onInput"
    @blur="onBlur"
  >
    <template v-slot:append>
      <div
        class="campo-quantidade-horas__modos"
        role="radiogroup"
        :aria-label="$t('campoQuantidadeHoras.Formato')"
      >
        <button
          v-for="opcao in opcoesModo"
          :key="opcao.valor"
          type="button"
          role="radio"
          :aria-checked="modo === opcao.valor ? 'true' : 'false'"
          :title="opcao.titulo"
          :disabled="disabled"
          :class="[
            'campo-quantidade-horas__modo',
            { 'campo-quantidade-horas__modo--ativo': modo === opcao.valor },
          ]"
          @mousedown.prevent
          @click="alterarModo(opcao.valor)"
        >
          {{ opcao.texto }}
        </button>
      </div>
    </template>
  </v-text-field>
</template>

<script>
import { translateIfMessageExists } from "@/utils/i18n";
import {
  formatarHorasDecimais,
  formatarHorasDecimaisTexto,
  horasDecimaisTextoParaNumero,
  horasInformadasParaDecimal,
} from "@/utils/horasPonto";

const MODO_HORA = "hora";
const MODO_DECIMAL = "decimal";
const CHAVE_MODO = "cajupe:campoQuantidadeHoras:modo";

function lerModoSalvo() {
  try {
    return window.localStorage.getItem(CHAVE_MODO) === MODO_DECIMAL
      ? MODO_DECIMAL
      : MODO_HORA;
  } catch (e) {
    return MODO_HORA;
  }
}

function salvarModo(modo) {
  try {
    window.localStorage.setItem(CHAVE_MODO, modo);
  } catch (e) {
    // Sem armazenamento (aba anônima, bloqueio): volta para HH:MM na próxima vez.
  }
}

function vazio(valor) {
  return (
    valor === null ||
    valor === undefined ||
    valor === "" ||
    Number.isNaN(Number(valor))
  );
}

// Quantidade de horas em HH:MM (padrão) ou decimal. O v-model sempre recebe o
// decimal que a API espera (08:30 = 8.5) ou null quando vazio ou inválido; o
// evento formato-invalido avisa o pai para bloquear o envio.
export default {
  name: "CampoQuantidadeHoras",

  props: {
    value: { type: [Number, String], default: null },
    label: { type: String, default: "QuantidadeHoras" },
    name: { type: String, default: "quantidadeHoras" },
    obrigatorio: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      MODO_DECIMAL,
      modo: lerModoSalvo(),
      texto: "",
      textoDoValor: "",
      valorDoTexto: null,
      ultimoValor: undefined,
      exibirErroFormato: false,
    };
  },

  computed: {
    translatedLabel() {
      return translateIfMessageExists(this.$i18n, this.label);
    },

    idioma() {
      return (this.$i18n && this.$i18n.locale) || "pt-BR";
    },

    opcoesModo() {
      return [
        {
          valor: MODO_HORA,
          texto: "HH:MM",
          titulo: this.$t("campoQuantidadeHoras.Hora_titulo"),
        },
        {
          valor: MODO_DECIMAL,
          texto: this.$t("campoQuantidadeHoras.Decimal"),
          titulo: this.$t("campoQuantidadeHoras.Decimal_titulo"),
        },
      ];
    },

    valorDigitado() {
      return this.interpretar(this.texto, this.modo);
    },

    // Valor que vai para a API. Enquanto o texto é o exibido a partir do valor
    // recebido, vale esse valor (8,33 exibido como 08:20 continua 8,33, e um
    // valor fora do formato não trava a edição); senão, o digitado.
    valorAtual() {
      if (this.texto.trim() === "") {
        return null;
      }
      if (this.texto === this.textoDoValor) {
        return this.valorDoTexto;
      }
      return this.valorDigitado;
    },

    formatoInvalido() {
      return this.texto.trim() !== "" && this.valorAtual === null;
    },

    placeholderText() {
      if (this.modo !== MODO_DECIMAL) {
        return "HH:MM";
      }
      const separador = formatarHorasDecimaisTexto(1.5, this.idioma).charAt(1);
      return `0${separador}0`;
    },

    hintText() {
      if (this.valorAtual === null) {
        return this.modo === MODO_DECIMAL
          ? this.$t("campoQuantidadeHoras.Exemplo_decimal", {
              valor: formatarHorasDecimaisTexto(8.5, this.idioma),
            })
          : this.$t("campoQuantidadeHoras.Exemplo_hora");
      }
      return this.modo === MODO_DECIMAL
        ? this.$t("campoQuantidadeHoras.Equivale_hora", {
            valor: formatarHorasDecimais(this.valorAtual),
          })
        : this.$t("campoQuantidadeHoras.Equivale_decimal", {
            valor: formatarHorasDecimaisTexto(this.valorAtual, this.idioma),
          });
    },

    validationRules() {
      if (!this.obrigatorio) return [];
      return [(valor) => !!valor || this.$t("Campo_obrigatorio")];
    },

    errorMessages() {
      if (!this.exibirErroFormato || !this.formatoInvalido) {
        return [];
      }
      return [this.mensagemFormatoInvalido()];
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(valor) {
        if (this.mesmoValor(valor, this.ultimoValor)) return;
        this.ultimoValor = vazio(valor) ? null : Number(valor);
        this.exibirValor(valor, this.modo);
        this.exibirErroFormato = false;
      },
    },

    formatoInvalido: {
      immediate: true,
      handler(invalido) {
        this.$emit("formato-invalido", invalido);
      },
    },
  },

  beforeDestroy() {
    // Campo oculto (outro tipo de afastamento) não pode travar o envio.
    if (this.formatoInvalido) {
      this.$emit("formato-invalido", false);
    }
  },

  methods: {
    interpretar(texto, modo) {
      return modo === MODO_DECIMAL
        ? horasDecimaisTextoParaNumero(texto)
        : horasInformadasParaDecimal(texto);
    },

    formatar(valor, modo) {
      if (vazio(valor)) {
        return "";
      }
      return modo === MODO_DECIMAL
        ? formatarHorasDecimaisTexto(valor, this.idioma)
        : formatarHorasDecimais(valor);
    },

    exibirValor(valor, modo) {
      this.texto = this.formatar(valor, modo);
      this.textoDoValor = this.texto;
      this.valorDoTexto = vazio(valor) ? null : Number(valor);
    },

    mesmoValor(a, b) {
      if (vazio(a) || vazio(b)) {
        return vazio(a) && vazio(b);
      }
      return Math.abs(Number(a) - Number(b)) < 1e-9;
    },

    emitir(valor) {
      if (this.mesmoValor(valor, this.ultimoValor)) return;
      this.ultimoValor = valor;
      this.$emit("input", valor);
    },

    onInput(texto) {
      this.texto = texto || "";
      if (!this.formatoInvalido) {
        this.exibirErroFormato = false;
      }
      this.emitir(this.valorAtual);
    },

    onBlur() {
      if (this.formatoInvalido) {
        this.exibirErroFormato = true;
        return;
      }
      // 830, 8h30 e 8.50 viram 08:30 / 8,5 sem alterar o valor enviado.
      if (this.valorAtual !== null) {
        this.exibirValor(this.valorAtual, this.modo);
      }
    },

    alterarModo(modo) {
      if (modo === this.modo) return;
      const anteriorValido = !this.formatoInvalido;
      const valor = this.valorAtual;
      this.modo = modo;
      salvarModo(modo);

      if (anteriorValido) {
        // Só muda a forma de exibir; o valor da API continua o mesmo.
        this.exibirValor(valor, modo);
        return;
      }
      // Texto que não servia no formato anterior (ex.: 8,5 com HH:MM) é lido
      // de novo no formato escolhido.
      this.onInput(this.texto);
      if (!this.formatoInvalido) {
        this.onBlur();
      }
    },

    mensagemFormatoInvalido() {
      if (this.modo === MODO_DECIMAL) {
        const emHora = horasInformadasParaDecimal(this.texto);
        return emHora !== null && /[:h]/i.test(this.texto)
          ? this.$t("campoQuantidadeHoras.Formato_invalido_decimal_hora", {
              valor: this.texto.trim(),
              decimal: formatarHorasDecimaisTexto(emHora, this.idioma),
            })
          : this.$t("campoQuantidadeHoras.Formato_invalido_decimal", {
              valor: formatarHorasDecimaisTexto(8.5, this.idioma),
            });
      }
      const emDecimal = horasDecimaisTextoParaNumero(this.texto);
      return emDecimal !== null && /[.,]/.test(this.texto)
        ? this.$t("campoQuantidadeHoras.Formato_invalido_hora_decimal", {
            valor: this.texto.trim(),
            hora: formatarHorasDecimais(emDecimal),
          })
        : this.$t("campoQuantidadeHoras.Formato_invalido_hora");
    },
  },
};
</script>

<style scoped>
.campo-quantidade-horas__modos {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
  padding: 2px;
  margin: -2px -6px -2px 4px;
  border-radius: 8px;
  background: #e9ecef;
}

.campo-quantidade-horas__modo {
  padding: 1px 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #67748e;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.6;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.campo-quantidade-horas__modo:hover:not(:disabled) {
  color: #344767;
}

.campo-quantidade-horas__modo:focus-visible {
  outline: 2px solid #5e72e4;
  outline-offset: 1px;
}

.campo-quantidade-horas__modo--ativo {
  background: #fff;
  color: #344767;
  box-shadow: 0 1px 3px rgba(52, 71, 103, 0.18);
}

.campo-quantidade-horas__modo:disabled {
  cursor: default;
  opacity: 0.6;
}
</style>
